import BaseTrigger from "./triggers/baseTrigger.js";
import { ASSIGNMENT_HINT_STEPS } from "./hints/assignment-hints.js";
import homeWelcomeScenario from "./scenarios/home-welcome.js";
import startGradingScenario, {
  START_GRADING_SCENARIO_STEPS,
} from "./scenarios/start-grading.js";
import {
  RUBRIC_LIBRARY_SCENARIO_PAGE,
  RUBRIC_LIBRARY_SCENARIO_SIDEBAR,
  RUBRIC_BUILDER_SCENARIO,
  CREATE_RUBRIC_FROM_FILE_SCENARIO,
  CREATE_RUBRIC_FROM_FILE_SCENARIO_STEPS,
} from "./scenarios/rubric-library.js";
import assignmentViewScenario from "./scenarios/assignment-view.js";
import lmsImportScenario, {
  LMS_IMPORT_SCENARIO_STEPS,
} from "./scenarios/lms-import.js";
import essayPageScenario from "./scenarios/essay-page.js";
import { metricsOnboardings } from "./hints/metrics-onboardings.js";

export const getOnboardings = ({ router } = {}) => {
  return [
    // ─── DASHBOARD ────────────────────────────────────────────────────────────
    // First-time welcome tour shown on the home/dashboard page.
    // Forced (non-skippable) for new users only.
    {
      key: "homeWelcome",
      trigger: new BaseTrigger("routeEnter", {
        route: { name: "dashboard" },
      }),
      scenario: homeWelcomeScenario,
    },

    // ─── ASSIGNMENT VIEW ──────────────────────────────────────────────────────
    // Tour for the assignment view page (workspace folder with essays).
    // Triggered when the assignment view component mounts.
    {
      key: "assignmentView",
      waitForElements: ["[data-testid='eg-selectable-table'] .status-badge"],
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:mounted:assignmentView",
      }),
      scenario: assignmentViewScenario(router),
    },

    // ─── RUBRIC LIBRARY ───────────────────────────────────────────────────────
    // Tour for the rubric library sidebar (opened from any page via rubric select).
    {
      key: "rubricLibrary",
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:opened:rubricLibrary",
      }),
      scenario: RUBRIC_LIBRARY_SCENARIO_SIDEBAR,
    },
    // Tour for the standalone Rubrics page (/rubrics route).
    {
      key: "rubricLibraryPage",
      trigger: new BaseTrigger("routeEnter", {
        route: { name: "rubrics" },
      }),
      scenario: RUBRIC_LIBRARY_SCENARIO_PAGE,
    },
    // Tour for the "Create Rubric from File" upload sidebar.
    // Triggered when the sidebar mounts after user chooses "Create from file".
    {
      key: "createRubricFromFile",
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:mounted:createRubricFromFile",
      }),
      scenario: CREATE_RUBRIC_FROM_FILE_SCENARIO,
    },
    // Tour for the Rubric Builder editor.
    // Triggered when the rubric builder sidebar/modal opens.
    {
      key: "rubricBuilder",
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:opened:rubricBuilder",
      }),
      scenario: RUBRIC_BUILDER_SCENARIO,
    },

    // ─── RUBRIC HINTS (single-step, triggered by ? icons) ────────────────────
    // Hint: "Create from file" button inside the rubric library sidebar.
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:createRubricFromFile",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [CREATE_RUBRIC_FROM_FILE_SCENARIO_STEPS.uploadOrImport],
      },
    },
    // Hint: Flexible scoring toggle inside rubric creation flow.
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:createRubricFlexibleScoring",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.gradingDetailsFlexibleScoring],
      },
    },

    // ─── MANUAL GRADING (Upload Essays) ──────────────────────────────────────
    // Main tour for the manual essay upload page (/manual-grading route).
    // Covers class/assignment, rubric, reference material, upload, grading details.
    {
      key: "startGrading",
      trigger: new BaseTrigger("routeEnter", {
        route: { name: "manual-grading" },
      }),
      scenario: startGradingScenario,
    },
    // Hints: individual ? icons on the manual grading form fields.
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormClassAssignmentQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.classAssignmentSelect],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormRubricSelectQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.rubricSelect],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormReferenceMaterialQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.referenceMaterial],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormUploadAreaQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.uploadArea],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormGradingDetailsQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.gradingDetails],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:submissionFormFlexibleScoringQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [START_GRADING_SCENARIO_STEPS.gradingDetailsFlexibleScoring],
      },
    },

    // ─── LMS IMPORT ───────────────────────────────────────────────────────────
    // Main tour for the LMS import page (/imports-assignments route).
    // Similar flow to manual grading but for LMS-connected submissions.
    {
      key: "lmsStartGrading",
      trigger: new BaseTrigger("routeEnter", {
        route: { name: "imports-assignments" },
      }),
      scenario: lmsImportScenario,
    },
    // Step 2 of LMS import: rubric selection and grading details sidebar.
    // Triggered when user proceeds to the second step of the LMS import flow.
    {
      key: "lmsImportStep2",
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:lmsImportStep2",
      }),
      scenario: {
        steps: [
          START_GRADING_SCENARIO_STEPS.rubricSelect,
          START_GRADING_SCENARIO_STEPS.gradingDetails,
        ],
      },
    },
    // Hints: individual ? icons on the LMS import form fields.
    {
      trigger: new BaseTrigger("vueEvent", {
        event:
          "onboarding:clicked:lmsSubmissionFormClassAssignmentQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [LMS_IMPORT_SCENARIO_STEPS.classAssignmentSelect],
      },
    },
    {
      trigger: new BaseTrigger("vueEvent", {
        event:
          "onboarding:clicked:lmsSubmissionFormSelectSubmissionsQuestionIcon",
      }),
      scenario: {
        showProgress: false,
        smoothScroll: false,
        steps: [LMS_IMPORT_SCENARIO_STEPS.selectSubmissions],
      },
    },

    // GRADE MORE SUBMISSIONS HINT
    // Click-triggered: always fires when user clicks the info icon
    {
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:clicked:gradeMoreSubmissionsHint",
      }),
      scenario: {
        showProgress: false,
        steps: [ASSIGNMENT_HINT_STEPS.gradeMoreSubmissions],
      },
    },
    // Auto-show once: fires on mount, skips if another tour is running, tracked in Clerk
    {
      key: "gradeMoreSubmissionsHint",
      skipIfRunning: true,
      trigger: new BaseTrigger("vueEvent", {
        event: "onboarding:graded:gradeMoreSubmissionsButton",
      }),
      scenario: {
        showProgress: false,
        steps: [ASSIGNMENT_HINT_STEPS.gradeMoreSubmissions],
      },
    },

    // ─── ESSAY PAGE ───────────────────────────────────────────────────────────
    // Tour for the individual essay grading/feedback page.
    {
      key: "essayPage",
      trigger: new BaseTrigger("routeEnter", {
        route: { name: "workspace-essay-view" },
      }),
      scenario: {
        showProgress: false,
        ...essayPageScenario,
      },
    },

    // ─── METRICS HINTS ────────────────────────────────────────────────────────
    // Single-step hints for metric card info icons (AI detection, plagiarism, etc.)
    ...metricsOnboardings,
  ];
};
